import React, { useEffect, useRef, useState } from "react";
import useForm from '../../../../hooks/useForm';
import { FormField, TextInput } from '@prosolve/common-components-ui'
import AnswerOptionsEditor from "./AnswerOptionsEditor";
import SoefiaConfigEditor from "./SoefiaConfigEditor";
import GenerateAudioModal from "./GenerateAudioModal";
import ConfirmationDialog from "../ConfirmationDialog";
import { FaRegTrashAlt, FaVolumeUp } from 'react-icons/fa';

export default ({
  question: questionObj = {},
  isReadOnly,
  onChange,
  onDelete,
  assessmentName
}) => {
  const formSchema = {
    requiredFields: ['id', 'question', 'type'],
    optionalFields: ['category', 'questionDescription', 'isRow', 'totalStars', 'validationRegex', 'validationNote', 'isOptional', 'isColumn', 'isSingleLineText', 'skill', 'strand', 'gradeBand', 'observable'],
    initData: questionObj
  }

  const tagsArrayToString = (tagsArray = []) => {
    return tagsArray.join(',')
  }

  const tagsStrToArray = (tagsStr = '') => {
    const tagsArray = tagsStr.split(',')
    return tagsArray
      .map(tag => tag.trim())
      .filter(tag => tag !== '')
  }

  // We handle tags outside of the form since it require extra customization to handle array to string conversion then back again
  const [tagsStr, setTagsStr] = useState(tagsArrayToString(questionObj.tags))

  const { answerOptions, soefiaConfig } = questionObj;

  const formData = useForm(formSchema)

  const [editedAnswerOptions, setEditedAnswerOptions] = useState(answerOptions)

  const [editedSoefiaConfig, setEditedSoefiaConfig] = useState(soefiaConfig)

  const [isGenerateAudioOpen, setIsGenerateAudioOpen] = useState(false)

  const [isRemoveAudioConfirmOpen, setIsRemoveAudioConfirmOpen] = useState(false)

  const [textToSpeechAudioUrl, setTextToSpeechAudioUrl] = useState(questionObj.textToSpeechAudioUrl)

  // Bumped on regeneration so the audio player reloads the overwritten file instead of the cached one
  const [audioCacheBuster, setAudioCacheBuster] = useState()

  const onAnswerOptionsChanged = (updateEditedAnswerOptions) => {
    setEditedAnswerOptions(updateEditedAnswerOptions)
  }

  const onSoefiaConfigChanged = (updateSoefiaConfig) => {
    setEditedSoefiaConfig(updateSoefiaConfig)
  }

  useEffect(() => {
    const changedValues = {
      ...formData.values
    }
    if (editedAnswerOptions) {
      changedValues.answerOptions = editedAnswerOptions;
    }
    if (editedSoefiaConfig) {
      changedValues.soefiaConfig = editedSoefiaConfig;
    }
    changedValues.textToSpeechAudioUrl = textToSpeechAudioUrl;
    if (changedValues.type !== 'stars') {
      delete changedValues.totalStars;
    }
    if (changedValues.type !== 'text') {
      delete changedValues.validationRegex;
      delete changedValues.validationNote;
    }
    if (changedValues.type !== 'multitext') {
      delete changedValues.isColumn;
    }

    const tagsArray = tagsStrToArray(tagsStr);
    changedValues.tags = tagsArray;

    onChange(changedValues)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.values, editedAnswerOptions, editedSoefiaConfig, tagsStr, textToSpeechAudioUrl])

  const currentQuestionType = formData.values.type;

  const typeOptions = [
    { value: 'radio', text: 'Radio'  },
    { value: 'checkbox', text: 'Checkbox' },
    { value: 'text', text: 'Text'  },
    { value: 'stars', text: 'Stars'  },
    { value: 'multitext', text: 'Multitext'  },
    { value: 'fileupload', text: 'File Upload'  },
    { value: 'soefia', text: 'Soefia'  },
  ]

  const [skillSearchVal, setSkillSearchVal] = useState('')

  const skillStrandsMap = {
    'Collaboration': ['Relationship Building', 'Contribution'],
    'Communication': ['Expression', 'Dialogue'],
    'Social Awareness': ['Empathy', 'Inclusion'],
    'Critical Thinking': ['Investigation', 'Judgment'],
    'Creative Thinking': ['Experimentation', 'Imagination'],
    'Systems Thinking': ['Interconnection', 'Leverage'],
    'Self-Awareness': ['Relational Awareness', 'Emotional Awareness'],
    'Self-Management': ['Emotional Regulation', 'Intentional Action'],
    'Self-Efficacy': ['Assessing', 'Strategizing'],
    'Curiosity': ['Wondering', 'Discovering'],
    'Perseverance': ['Motivating', 'Persisting'],
    'Resilience': ['Facing', 'Learning'],
    'Agency & Ownership': ['Confidence & Self-Direction', 'Voice & Choice'],
    'Connectedness': ['Belonging', 'Inclusion & Acceptance'],
    'Positive Experience & Relevance': ['Enjoyment & Interest', 'Relevance & Value'],
    'Positive Relationships': ['Peer Kindness & Respect', 'Teacher Care & Trust'],
  }

  const skillOptions = [
    { value: '', text: '\u00A0' },
    ...Object.keys(skillStrandsMap).map(skill => ({ value: skill, text: skill }))
  ]

  const filteredSkillOptions = skillOptions.filter(opt => {
    if(skillSearchVal) {
      if (opt.text.toLowerCase().indexOf(skillSearchVal.toLowerCase()) > -1) {
        return true
      } else {
        return false
      }
    } else {
      return true
    }
  })

  const [strandSearchVal, setStrandSearchVal] = useState('')

  const selectedSkill = formData.values.skill;
  const strandsForSkill = skillStrandsMap[selectedSkill] || [];

  const strandOptions = [
    { value: '', text: '\u00A0' },
    ...strandsForSkill.map(strand => ({ value: strand, text: strand }))
  ]

  const strandFieldRef = useRef();
  const prevSkillRef = useRef(formData.values.skill);

  // When the skill changes, clear the strand so only the new skill's strands can be chosen
  useEffect(() => {
    if (prevSkillRef.current !== formData.values.skill) {
      prevSkillRef.current = formData.values.skill;
      if (strandFieldRef.current) {
        strandFieldRef.current.clearInput();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.values.skill])

  const gradeBandOptions = [
    { value: '', text: '\u00A0' },
    { value: 'K-2', text: 'K-2' },
    { value: '3-5', text: '3-5' },
    { value: '6-8', text: '6-8' },
    { value: '9-12', text: '9-12' },
  ]

  const filteredStrandOptions = strandOptions.filter(opt => {
    if(strandSearchVal) {
      if (opt.text.toLowerCase().indexOf(strandSearchVal.toLowerCase()) > -1) {
        return true
      } else {
        return false
      }
    } else {
      return true
    }
  })

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #dedede',
      borderRadius: '5px',
      // marginBottom: '20px',
      position: 'relative'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        borderBottom: '1px solid #dedede',
        background: '#f8f9fc',
        padding: '20px',
        borderTopLeftRadius: '5px',
        borderTopRightRadius: '5px',
        // borderRadius: '5px',
        // marginBottom: '20px',
      }}>
        <div>
          <FaRegTrashAlt 
            onClick={() => !isReadOnly && onDelete(formData.values)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              fontSize: '20px',
              cursor: isReadOnly ? 'default' : 'pointer',
              color: isReadOnly ? '#7E7E7E' : '#f85c5c'
            }}  
          />
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          columnGap: '15px',
          rowGap: '20px',
          marginBottom: '20px'
        }}>
          <FormField
            fieldId='question'
            type='text'
            label='Question'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
          {/* <FormField
            fieldId='category'
            type='text'
            label='Category'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          /> */}
        </div>
        {
          (!isReadOnly || Boolean(textToSpeechAudioUrl)) &&
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            rowGap: '10px',
            marginBottom: '20px'
          }}>
            {
              !isReadOnly &&
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '5px',
                  fontSize: '14px',
                  color: formData.values.question ? '#0280c6' : '#7e7e7e',
                  cursor: formData.values.question ? 'pointer' : 'default',
                  textDecoration: 'underline'
                }}
                onClick={() => formData.values.question && setIsGenerateAudioOpen(true)}
              >
                <FaVolumeUp />
                {'Generate Audio (Text-to-Speech)'}
              </div>
            }
            {
              Boolean(textToSpeechAudioUrl) &&
              <div style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: '15px',
                rowGap: '10px'
              }}>
                <audio controls src={audioCacheBuster ? `${textToSpeechAudioUrl}?t=${audioCacheBuster}` : textToSpeechAudioUrl} />
                {
                  !isReadOnly &&
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      columnGap: '5px',
                      fontSize: '14px',
                      color: '#f85c5c',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                    onClick={() => setIsRemoveAudioConfirmOpen(true)}
                  >
                    <FaRegTrashAlt />
                    {'Remove Audio'}
                  </div>
                }
              </div>
            }
          </div>
        }
        <div style={{ display: 'flex', marginBottom: '20px' }}>
          <FormField
            fieldId='questionDescription'
            type='richtext'
            label='Question Description'
            disabled={isReadOnly}
            style={{ flex: 1 }}
            formData={formData}
          />
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          columnGap: '30px',
          rowGap: '20px',
          marginBottom: '20px'
        }}>
          <FormField
            fieldId='type'
            type='select'
            label='Type'
            disabled={isReadOnly}
            // style={{ width: '100px' }}
            formData={formData}
            options={typeOptions}
          />
          {
            formData.values.type === 'stars' &&
            <FormField
              fieldId='totalStars'
              type='select'
              label='Total Stars'
              disabled={isReadOnly}
              formData={formData}
              options={[{ value: 10, text: '10'  }, { value: 5, text: '5'  }]}
            />
          }
          <FormField
            fieldId='isRow'
            type='checkbox'
            label='Is Row'
            disabled={isReadOnly}
            // style={{ flex: 1 }}
            formData={formData}
          />
          <FormField
            fieldId='isOptional'
            type='checkbox'
            label='Is Optional (Not Required)'
            disabled={isReadOnly}
            // style={{ flex: 1 }}
            formData={formData}
          />
          {
            formData.values.type === 'multitext' &&
            <FormField
              fieldId='isColumn'
              type='checkbox'
              label='Is Column'
              disabled={isReadOnly}
              // style={{ flex: 1 }}
              formData={formData}
            />
          }
          {
            formData.values.type === 'text' &&
            <FormField
              fieldId='isSingleLineText'
              type='checkbox'
              label='Is Single Line Text'
              disabled={isReadOnly}
              // style={{ flex: 1 }}
              formData={formData}
            />
          }
        </div>
        {
          ['PulseCheck', 'TeacherPulseCheck'].includes(assessmentName) &&
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            // alignItems: 'flex-end',
            flexWrap: 'wrap',
            columnGap: '30px',
            rowGap: '20px',
            marginBottom: '20px'
          }}>
            <FormField
              fieldId='skill'
              type='search'
              label='Skill'
              disabled={isReadOnly}
              style={{ flex: 1 }}
              maxHeight='300px'
              width='100%'
              formData={formData}
              options={filteredSkillOptions}
              onSearch={(val) => setSkillSearchVal(val)}
            />
            <FormField
              ref={strandFieldRef}
              fieldId='strand'
              type='search'
              label='Strand'
              disabled={isReadOnly || !selectedSkill}
              style={{ flex: 1 }}
              maxHeight='300px'
              width='100%'
              formData={formData}
              options={filteredStrandOptions}
              onSearch={(val) => setStrandSearchVal(val)}
            />
          </div>
        }
        {
          ['PulseCheck', 'TeacherPulseCheck'].includes(assessmentName) &&
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            columnGap: '30px',
            rowGap: '20px',
            marginBottom: '20px'
          }}>
            <FormField
              fieldId='gradeBand'
              type='select'
              label='Grade Band'
              disabled={isReadOnly}
              style={{ flex: 1 }}
              width='100%'
              formData={formData}
              options={gradeBandOptions}
            />
            <FormField
              fieldId='observable'
              type='text'
              label='Observable'
              disabled={isReadOnly}
              style={{ flex: 1 }}
              formData={formData}
            />
          </div>
        }
        {
          formData.values.type === 'text' &&
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            columnGap: '30px',
            rowGap: '20px',
            marginBottom: '20px'
          }}>
            <FormField
              fieldId='validationRegex'
              type='text'
              label='Validation Regex'
              disabled={isReadOnly}
              formData={formData}
              style={{ flex: 1 }}
            />
            <FormField
              fieldId='validationNote'
              type='text'
              label='Validation Note'
              disabled={isReadOnly}
              formData={formData}
              style={{ flex: 1 }}
            />
          </div>
        }
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          columnGap: '30px',
          rowGap: '20px',
        }}>
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              marginBottom: '5px',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#565656',
            }}>
              {'Tags'}
            </div>
            <TextInput
              width='100%'
              value={tagsStr}
              onChange={(val) => setTagsStr(val)}
            />
          </div>
        </div>
      </div>
      {
        ['radio','checkbox','multitext'].includes(currentQuestionType) &&
          <AnswerOptionsEditor
            answerOptions={answerOptions}
            onChange={onAnswerOptionsChanged}
            isReadOnly={isReadOnly}
            type={currentQuestionType}
          />
      }
      {
        currentQuestionType === 'soefia' &&
        <SoefiaConfigEditor
          soefiaConfig={soefiaConfig}
          onChange={onSoefiaConfigChanged}
          isReadOnly={isReadOnly}
        />
      }
      {
        Boolean(isGenerateAudioOpen) &&
        <GenerateAudioModal
          assessmentName={assessmentName}
          questionId={formData.values.id}
          questionText={formData.values.question}
          onClose={() => setIsGenerateAudioOpen(false)}
          onSuccess={(fileUrl) => {
            setTextToSpeechAudioUrl(fileUrl)
            setAudioCacheBuster(Date.now())
            setIsGenerateAudioOpen(false)
          }}
        />
      }
      {
        Boolean(isRemoveAudioConfirmOpen) &&
        <ConfirmationDialog
          title='Remove Audio'
          text='This will remove the text-to-speech audio link from this question.'
          onClose={() => setIsRemoveAudioConfirmOpen(false)}
          onSubmit={() => {
            setTextToSpeechAudioUrl('')
            setIsRemoveAudioConfirmOpen(false)
          }}
        />
      }

    </div>
  )
}